import React, { useState, useEffect, useMemo } from "react";
import { arrayOf, string, shape, number } from "prop-types";
import throttle from "lodash.throttle";
import cx from "classnames";

import Icon from "../Icon/Icon.jsx";
import inPageNavigation from "../../common/inPageNavigation.js";

const propTypes = {
  links: arrayOf(
    shape({
      index: number.isRequired,
      label: string.isRequired,
      href: string.isRequired,
    }),
  ).isRequired,
  label: string,
};

const ChapterNavigation = (props) => {
  const { links, label } = props;

  const targets = useMemo(() => {
    return links.map((link) => document.querySelector(link.href));
  }, [links]);

  const navHelper = inPageNavigation();

  const [activeLinkIndex, setActiveLinkIndex] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);

  const handleLinkClick = (e, index) => {
    e.preventDefault();
    setIsScrolling(true);
    navHelper.scrollPageTo(targets[index]);

    setTimeout(() => {
      setActiveLinkIndex(index);
      setIsScrolling(false);
    }, 1000);
  };

  useEffect(() => {
    const throttleHandleScroll = throttle((event) => {
      if (isScrolling) {
        return;
      }

      const topOffset = window.innerHeight / 2 + 100;

      const index = [...targets].reduce((index, target) => {
        const rect = target.getBoundingClientRect();
        return rect.top < topOffset ? parseInt(target.dataset.index) : index;
      }, -1);

      if (index !== activeLinkIndex) {
        const hash = index >= 0 ? links[index].href : "";
        const url = window.location.hash
          ? window.location.href.replace(/#.*$/, hash)
          : window.location.href + hash;
        history.replaceState("", document.title, url);
        event.preventDefault();
        setActiveLinkIndex(index);
      }
    }, 100);

    window.addEventListener("scroll", throttleHandleScroll);

    return () => {
      window.removeEventListener("scroll", throttleHandleScroll);
    };
  }, [activeLinkIndex, isScrolling, links, navHelper, targets]);

  const previousLink = activeLinkIndex > 0 ? links[activeLinkIndex - 1] : null;
  const nextLink =
    activeLinkIndex < links.length ? links[activeLinkIndex + 1] : null;

  return (
    <div className="chapter-navigation__app">
      <div className="chapter-navigation__container container">
        <div className="chapter-navigation__items">
          {links.map((link, i) => {
            return (
              <div
                key={i}
                className={cx("chapter-navigation__item", {
                  "chapter-navigation__item--before": i < activeLinkIndex,
                  "chapter-navigation__item--current": i === activeLinkIndex,
                  "chapter-navigation__item--next": i === activeLinkIndex + 1,
                  "chapter-navigation__item--after": i > activeLinkIndex + 1,
                })}
              >
                {i === activeLinkIndex + 1 && (
                  <span className="chapter-navigation__label">{label}</span>
                )}
                <span className="chapter-navigation__name">{link.label}</span>
              </div>
            );
          })}
        </div>
        <div className="chapter-navigation__controls">
          {!previousLink && (
            <div
              aria-hidden={true}
              className="chapter-navigation__prev chapter-navigation__prev--disabled"
            >
              <Icon iconName="chevron-right" className="icon--24x24" />
            </div>
          )}
          {previousLink && (
            <a
              className="chapter-navigation__prev"
              title={previousLink.label}
              href={previousLink.href}
              onClick={(e) => handleLinkClick(e, previousLink.index)}
            >
              <Icon iconName="chevron-right" className="icon--24x24" />
            </a>
          )}
          {nextLink && (
            <a
              className="chapter-navigation__next"
              title={nextLink.label}
              href={nextLink.href}
              onClick={(e) => handleLinkClick(e, nextLink.index)}
            >
              <Icon iconName="chevron-right" className="icon--24x24" />
            </a>
          )}
          {!nextLink && (
            <div
              aria-hidden={true}
              className="chapter-navigation__next chapter-navigation__next--disabled"
            >
              <Icon iconName="chevron-right" className="icon--24x24" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
ChapterNavigation.propTypes = propTypes;
export default ChapterNavigation;
